import { MenuGroup } from '@atlaskit/menu';
import { type PopupProps, type TriggerProps } from '@atlaskit/popup';
import Popup from '@databases/atlaskit/Popup';
import { useComponent } from '@databases/react';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { selectSelectedFieldIds } from '@databases/redux/cellSelections/selectors';
import { getDatabaseFieldRequired } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, {
  type DetailedHTMLProps,
  type FunctionComponent,
  type HTMLAttributes,
  useEffect,
  useState,
} from 'react';

import DatabaseQuickCalcValueEdit from './DatabaseQuickCalcValueEdit';

type TriggerPropsWithHtmlProps = TriggerProps &
  DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

type QuickCalculationValuesProps = {
  contentId: string;
  Trigger: (props: TriggerPropsWithHtmlProps) => React.ReactElement;
  isOpen: boolean;
  offset?: [number, number];
  placement?: PopupProps['placement'];
  onSelect: () => void;
};

const QuickCalculationValues: FunctionComponent<QuickCalculationValuesProps> = ({
  contentId,
  Trigger,
  isOpen,
  offset,
  placement,
  onSelect,
}) => {
  const firstSelectedField = defaultStoreUtils.useEqualSelector(
    (state: DefaultRootState) => {
      const database = selectDatabaseByContentIdRequired(state, {
        contentId,
      });
      const selectedFields = selectSelectedFieldIds(state, {
        contentId,
      });

      return getDatabaseFieldRequired(database, selectedFields[0]);
    }
  );

  const [isMenuOpen, setIsMenuOpen] = useState(isOpen);

  useEffect(() => {
    setIsMenuOpen(isOpen);
  }, [isOpen]);

  const trigger: PopupProps['trigger'] = useComponent(
    ({ ref, ...props }: TriggerProps) => {
      return Trigger({
        ref,
        ...props,
      });
    },
    [Trigger]
  );

  return (
    <Popup
      id="quick-calc-menu"
      autoFocus={false}
      isOpen={isMenuOpen}
      onClose={() => setIsMenuOpen(false)}
      trigger={trigger}
      placement={placement}
      contentContainer={null}
      offset={offset}
      content={({ setInitialFocusRef }) => (
        <MenuGroup
          minWidth="200px"
          maxHeight="435px"
          onClick={(e) => e.stopPropagation()}
        >
          <DatabaseQuickCalcValueEdit
            field={firstSelectedField}
            contentId={contentId}
            ref={setInitialFocusRef}
            onClose={() => {
              setIsMenuOpen(false);
              onSelect();
            }}
          />
        </MenuGroup>
      )}
    />
  );
};

export default QuickCalculationValues;
